import React, { useEffect, useMemo, useState } from "react";
import CustomToolbar from "../global/CustomToolbar";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import GetAppIcon from "@material-ui/icons/GetApp";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import RefreshIcon from "@material-ui/icons/Refresh";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import BreakdownDialog from "./BreakdownDialog";
import ConfirmDialog from "./ConfirmDialog";
import Spinner from "./Spinner";
import {
  MONTHS, money0, money2, weekRange, weeksInMonth, toISO, newItemId, sum, computeShare,
} from "./utils";

/* ═══════════════════════════════════════════════════════════
   Tiny shared UI atoms
   ══════════════════════════════════════════════════════════ */

function PTToolbar({ title, onBack, actions }) {
  const headerButtons = [];
  if (onBack) {
    headerButtons.push({
      icon: <KeyboardBackspaceIcon />,
      title: "Back",
      handler: onBack,
    });
  }
  (actions || []).forEach((a) => headerButtons.push(a));
  return <CustomToolbar title={title} headerButtons={headerButtons} />;
}

// ─── Delivery PDF ──────────────────────────────────────────
// Mirrors LizAccounting.downloadDeliveryPDF — generates a styled
// "How It Was Delivered" table and triggers a browser download.
function downloadDeliveryPdf({ locations, week, year }) {
  const rows = (locations || []).map((l) => {
    const d = l.delivery || {};
    const cash = Number(d.cash) || 0;
    const zelle = Number(d.managerZelle) || 0;
    const atmReceipts = Number(d.atmReceipts) || 0;
    const atmAdv = Number(d.atmAdv) || 0;
    return {
      locationName: l.name,
      cash, zelle, atmReceipts, atmAdv,
      totalDelivered: cash + zelle + atmReceipts + atmAdv,
    };
  });

  const doc = new jsPDF();
  const fmt = (val) =>
    `$${Number(val).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  doc.setFontSize(18);
  doc.setTextColor(40, 40, 40);
  doc.text("How It Was Delivered", 14, 20);

  doc.setFontSize(11);
  doc.setTextColor(100, 100, 100);
  const weekLabel = week ? `${week.id} · ${week.label}, ${year}` : "Delivery Summary";
  doc.text(weekLabel, 14, 28);

  const totals = rows.reduce(
    (acc, r) => ({
      cash: acc.cash + r.cash,
      zelle: acc.zelle + r.zelle,
      atmReceipts: acc.atmReceipts + r.atmReceipts,
      atmAdv: acc.atmAdv + r.atmAdv,
      totalDelivered: acc.totalDelivered + r.totalDelivered,
    }),
    { cash: 0, zelle: 0, atmReceipts: 0, atmAdv: 0, totalDelivered: 0 },
  );

  autoTable(doc, {
    startY: 35,
    head: [["Location", "Cash", "Zelle", "ATM Receipts", "ATM Adv", "Total Delivered"]],
    body: [
      ...rows.map((r) => [
        r.locationName,
        fmt(r.cash),
        fmt(r.zelle),
        fmt(r.atmReceipts),
        fmt(r.atmAdv),
        fmt(r.totalDelivered),
      ]),
      [
        { content: "TOTAL", styles: { fontStyle: "bold" } },
        { content: fmt(totals.cash), styles: { fontStyle: "bold" } },
        { content: fmt(totals.zelle), styles: { fontStyle: "bold" } },
        { content: fmt(totals.atmReceipts), styles: { fontStyle: "bold" } },
        { content: fmt(totals.atmAdv), styles: { fontStyle: "bold" } },
        { content: fmt(totals.totalDelivered), styles: { fontStyle: "bold" } },
      ],
    ],
    headStyles: { fillColor: [45, 90, 61], textColor: 255, fontStyle: "bold", fontSize: 10 },
    bodyStyles: { fontSize: 10, textColor: [40, 40, 40] },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    styles: { cellPadding: 5, lineColor: [200, 200, 220], lineWidth: 0.2 },
    columnStyles: { 0: { cellWidth: 40 } },
  });

  const safeWeek = week ? `${year}_W${week.weekNumber}` : "summary";
  doc.save(`delivery_${safeWeek}.pdf`);
}

function SectionCard({
  title, color, items, sign, totalOverride, totalLabel = "Total", defaultOpen = true,
  editable, onAdd, onRemove, onEditItem, renderRowClick, children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [newDesc, setNewDesc] = useState("");
  const [newAmt, setNewAmt] = useState("");

  const total = totalOverride ?? sum(items, (x) => x.amount);
  const signClass = sign === "-" ? "var(--pt-red)" : "var(--pt-green)";

  const doAdd = () => {
    if (!newDesc.trim()) return;
    const v = Number(newAmt);
    if (!Number.isFinite(v) || v === 0) return;
    if (onAdd) onAdd({ description: newDesc.trim(), amount: Math.abs(v), isManual: true });
    setNewDesc("");
    setNewAmt("");
  };

  return (
    <div className="pt-sec">
      <button className="pt-sec-head" onClick={() => setOpen(!open)}>
        <span className="title" style={{ color }}>{title}</span>
        <span style={{ color: "var(--pt-muted-2)", fontSize: 11 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="pt-sec-body">
          {items.length === 0 && !editable && (
            <div style={{ padding: "6px 0", color: "var(--pt-muted-2)", fontSize: 11 }}>
              No items.
            </div>
          )}
          {items.map((it, idx) => {
            const isNds = !it.isManual;
            if (editable && !isNds) {
              return (
                <div key={it._id || idx} className="pt-edit-item">
                  <input
                    value={it.description || ""}
                    onChange={(e) => onEditItem && onEditItem(idx, { description: e.target.value })}
                    placeholder="Description"
                  />
                  <input
                    className="amount"
                    type="number"
                    value={it.amount ?? 0}
                    onChange={(e) => onEditItem && onEditItem(idx, { amount: Number(e.target.value) || 0 })}
                  />
                  <button className="del" onClick={() => onRemove && onRemove(idx)} title="Remove">✕</button>
                </div>
              );
            }
            const clickable = !!renderRowClick && !!it._breakdown;
            return (
              <div
                key={it._id || idx}
                className="pt-sec-row"
                onClick={clickable ? () => renderRowClick(it) : undefined}
                style={clickable ? { cursor: "pointer" } : undefined}
              >
                <span className="l">
                  {it.description}
                  {clickable && (
                    <span style={{ color: "var(--pt-muted)", fontSize: 10, marginLeft: 6, textDecoration: "underline" }}>
                      view breakdown
                    </span>
                  )}

                </span>
                <span className="a" style={{ color: signClass }}>
                  {sign}{money2(it.amount)}
                </span>
              </div>
            );
          })}

          {editable && (
            <div className="pt-edit-add">
              <input
                className="desc"
                placeholder="Add description"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doAdd()}
              />
              <input
                className="amt"
                placeholder="Amount"
                type="number"
                value={newAmt}
                onChange={(e) => setNewAmt(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doAdd()}
              />
              <button onClick={doAdd}>+ Add</button>
            </div>
          )}

          {children}

          <div className="pt-sec-total">
            <span style={{ color }}>{totalLabel}</span>
            <span style={{ color: signClass }}>{sign}{money2(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Month list
   ══════════════════════════════════════════════════════════ */

export function MonthsScreen({ nav, ctx, currentWeek }) {
  const { year, mode } = ctx;
  const now = new Date();

  return (
    <>
      <PTToolbar title={`Year ${year}`} onBack={() => nav("landing")} />
      <div className="pt-content">
        {MONTHS.map((m, idx) => {
          const month1 = idx + 1;
          const isFuture = year === now.getFullYear() && month1 > now.getMonth() + 1;
          const isCurrent =
            year === currentWeek.year && month1 === currentWeek.month;

          return (
            <button
              key={m}
              disabled={isFuture}
              className={`pt-listbtn ${isCurrent ? "current" : ""}`}
              onClick={() => !isFuture && nav("weeks", { year, month: m, mode })}
            >
              <div>
                <div className="name">{m}</div>
                <div className="meta">{isFuture ? "No data yet" : "Accounting Month"}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {isCurrent && <span className="pt-badge">Latest</span>}
                {!isFuture && <span style={{ color: "var(--pt-muted-2)", fontSize: 16 }}>›</span>}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   Week list
   ══════════════════════════════════════════════════════════ */

export function WeeksScreen({ nav, ctx, currentWeek }) {
  const { year, month, mode } = ctx;
  const month1 = MONTHS.indexOf(month) + 1;
  const weeks = useMemo(() => weeksInMonth(year, month1), [year, month1]);

  return (
    <>
      <PTToolbar
        title={`${month} ${year}`}
        onBack={() => nav("months", { year, mode })}
      />
      <div className="pt-content">
        {weeks.length === 0 && (
          <div className="pt-empty">No weeks in this month.</div>
        )}
        {weeks.map((w) => {
          const isCurrent =
            year === currentWeek.year &&
            month1 === currentWeek.month &&
            currentWeek.week &&
            w.weekNumber === currentWeek.week.weekNumber;
          return (
            <button
              key={w.id}
              className={`pt-listbtn ${isCurrent ? "current" : ""}`}
              onClick={() => nav("detail", { year, month, week: w, mode })}
            >
              <div>
                <div className="name">
                  {w.id}{" "}
                  <span style={{ color: "var(--pt-muted)", fontWeight: 500, fontSize: 13 }}>
                    ({w.label})
                  </span>
                </div>
                <div className="meta">Accounting Week</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {isCurrent && <span className="pt-badge">Latest</span>}
                <span style={{ color: "var(--pt-muted-2)", fontSize: 16 }}>›</span>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   Weekly Detail — 6 tabs
   ══════════════════════════════════════════════════════════ */

const WEEKLY_TABS = [
  { id: "home", label: "Home" },
  { id: "locations", label: "Locations" },
  { id: "eric", label: "Eric" },
  { id: "liz", label: "Liz" },
  { id: "atm", label: "ATM" },
  { id: "delivery", label: "Delivery" },
];

export function WeeklyDetail({ nav, ctx, api, locations, isAdmin }) {
  const { year, month, week } = ctx;
  const month1 = MONTHS.indexOf(month) + 1;
  const [tab, setTab] = useState("home");
  const [weekData, setWeekData] = useState(null);
  const [summary, setSummary] = useState(null); // locationSummary response
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // edit-mode buckets (kept locally; saved in one POST)
  const [ericAdditions, setEricAdditions] = useState([]);
  const [ericSubtractions, setEricSubtractions] = useState([]);
  const [lizAdditions, setLizAdditions] = useState([]);
  const [lizSubtractions, setLizSubtractions] = useState([]);
  const [ericAtmUsed, setEricAtmUsed] = useState(0);
  const [lizCashInHand, setLizCashInHand] = useState(0);
  const [editing, setEditing] = useState({ eric: false, liz: false });
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState(null);
  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const locationNames = useMemo(() => locations.map((l) => l.name), [locations]);

  // ATM data hook lives here so Refresh + Save can be surfaced on the
  // Profit Tracker toolbar while the user is on the ATM sub-tab (same UX
  // as Eric/Liz). Still just one useATMData call per week — its effect
  // is keyed on week + locationNames.
  const atmData = useATMData(ctx, api, locationNames);
  const [atmRefreshConfirmOpen, setAtmRefreshConfirmOpen] = useState(false);

  useEffect(() => {
    if (!week) return;
    setLoading(true);
    setError(null);
    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);

    let gotSummary = false;
    let gotWeek = false;
    const done = () => {
      if (gotSummary && gotWeek) setLoading(false);
    };

    api.fetchLocationSummary(
      { fromDate, toDate, locationNames },
      (res) => {
        setSummary(res?.sessions ? res : { sessions: res?.sessions || res || [] });
        gotSummary = true;
        done();
      },
      (err) => {
        setError(typeof err === "string" ? err : "Failed to load location summary");
        gotSummary = true;
        done();
      },
    );

    api.fetchWeek(
      { year, month: month1, weekNumber: week.weekNumber },
      (res) => {
        const w = res?.week || null;
        setWeekData(w);
        if (w) {
          const stamp = (arr, bucket) =>
            (arr || []).map((x) => {
              const base = {
                _id: x._id || x.id || newItemId(),
                description: x.description ?? x.desc ?? "",
                amount: Number(x.amount) || 0,
                locationName: x.locationName || "",
                isManual: x.isManual === true,
                _source: bucket,
              };
              base.category = x.category || inferCategory(base, bucket);
              return base;
            });
          setEricAdditions(stamp(w.ericAdditions, "ericAdd"));
          setEricSubtractions(stamp(w.ericSubtractions, "ericSub"));
          setLizAdditions(stamp(w.lizAdditions, "lizAdd"));
          setLizSubtractions(stamp(w.lizSubtractions, "lizSub"));
          setEricAtmUsed(Number(w.ericExtra?.atm) || 0);
          setLizCashInHand(Number(w.lizCashInHand) || 0);
        }
        gotWeek = true;
        done();
      },
      (err) => {
        // no saved week is fine — treat as empty
        setWeekData(null);
        gotWeek = true;
        done();
      },
    );
  }, [year, month1, week, api, locationNames]);

  // derive NDS items from locationSummary if we don't have a saved week yet.
  // This mirrors the backfill behaviour of the old LizAccounting page on first open.
  useEffect(() => {
    if (!summary || weekData) return;
    const {
      ericAdds, ericSubs, lizAdds, lizSubs,
      totalCashInHand, totalOwesAmount,
    } = deriveNdsBuckets(summary.sessions || []);
    setEricAdditions(ericAdds);
    setEricSubtractions(ericSubs);
    setLizAdditions(lizAdds);
    setLizSubtractions(lizSubs);
    // Eric's "Owes Me From ADV ATM" line = totalOwesAmount
    // (sum of ATM_ADV + ATM_DIDNT_USE) per LizAccounting.ericExtra.owes.
    setEricAtmUsed(totalOwesAmount);
    setLizCashInHand(totalCashInHand);
  }, [summary, weekData]);

  // locations with their netProfit for this week (derived from locationSummary)
  const liveLocations = useMemo(() => {
    const sessions = summary?.sessions || [];
    const byName = new Map();
    for (const s of sessions) {
      const ms = s.managerSummary || {};
      byName.set(s.locationName, {
        name: s.locationName,
        netProfit: Number(ms.weeklyNetProfit) || 0,
        delivery: ms.delivery || {},
        atm: (ms.atmTransactions || []).reduce(
          (acc, t) => {
            if (t.type === "ATM_CASH") acc.cash += Number(t.amount) || 0;
            else if (t.type === "ATM_ADV") acc.adv += Number(t.amount) || 0;
            else if (t.type === "ATM_FROM_PROFIT") acc.profit += Number(t.amount) || 0;
            else if (t.type === "ATM_DIDNT_USE") acc.didntUse += Number(t.amount) || 0;
            return acc;
          },
          { cash: 0, adv: 0, profit: 0, didntUse: 0 },
        ),
        manager: ms.managerName || "",
      });
    }
    return locations.map((l) => ({
      ...l,
      ...(byName.get(l.name) || {
        netProfit: 0,
        delivery: {},
        atm: { cash: 0, adv: 0, profit: 0, didntUse: 0 },
        manager: "",
      }),
    }));
  }, [locations, summary]);

  const handleSave = () => {
    setSaving(true);
    setSaveNote(null);
    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);
    const stripInternal = (arr) =>
      arr.map(({ _id, _source, ...rest }) => rest);
    const payload = {
      year,
      month: month1,
      weekNumber: week.weekNumber,
      fromDate: toISO(fromDate),
      toDate: toISO(toDate),
      weeklyProfits: weekData?.weeklyProfits || [],
      unassignedAdditions: weekData?.unassignedAdditions || [],
      unassignedSubtractions: weekData?.unassignedSubtractions || [],
      unassignedOthers: weekData?.unassignedOthers || [],
      ericAdditions: stripInternal(ericAdditions),
      ericSubtractions: stripInternal(ericSubtractions),
      lizAdditions: stripInternal(lizAdditions),
      lizSubtractions: stripInternal(lizSubtractions),
      ericExtra: { atm: ericAtmUsed, owes: 0 },
      lizCashInHand,
    };
    api.saveWeek(
      payload,
      () => {
        setSaving(false);
        setSaveNote({ kind: "ok", text: "Saved" });
        setEditing({ eric: false, liz: false });
      },
      (err) => {
        setSaving(false);
        setSaveNote({ kind: "err", text: typeof err === "string" ? err : "Save failed" });
      },
    );
  };

  // Refresh — matches GM Profits' refresh flow (LizAccounting.applyDateRange +
  // restore manual entries). Re-pulls LOCATION_SUMMARY, rebuilds every NDS
  // bucket (Eric/Liz additions + subtractions), then appends any user-added
  // (isManual === true) rows the user had pending so their work isn't lost.
  const handleRefresh = () => {
    if (!week) return;
    setRefreshing(true);
    setSaveNote(null);
    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);
    const keepManual = (arr) => (arr || []).filter((x) => x.isManual === true);
    const manualBackup = {
      ericAdd: keepManual(ericAdditions),
      ericSub: keepManual(ericSubtractions),
      lizAdd: keepManual(lizAdditions),
      lizSub: keepManual(lizSubtractions),
    };

    api.fetchLocationSummary(
      { fromDate, toDate, locationNames },
      (res) => {
        const sessions = res?.sessions || [];
        setSummary({ sessions });
        const {
          ericAdds, ericSubs, lizAdds, lizSubs,
          totalCashInHand, totalOwesAmount,
        } = deriveNdsBuckets(sessions);
        setEricAdditions([...ericAdds, ...manualBackup.ericAdd]);
        setEricSubtractions([...ericSubs, ...manualBackup.ericSub]);
        setLizAdditions([...lizAdds, ...manualBackup.lizAdd]);
        setLizSubtractions([...lizSubs, ...manualBackup.lizSub]);
        setEricAtmUsed(totalOwesAmount);
        setLizCashInHand(totalCashInHand);
        setRefreshing(false);
        setSaveNote({ kind: "ok", text: "Week data refreshed" });
        setTimeout(() => setSaveNote(null), 2500);
      },
      (err) => {
        setRefreshing(false);
        setSaveNote({ kind: "err", text: typeof err === "string" ? err : "Refresh failed" });
      },
    );
  };

  const isPartnerTab = tab === "eric" || tab === "liz";
  const isPartnerEditing = tab === "eric" ? editing.eric : tab === "liz" ? editing.liz : false;
  const toolbarActions = [];
  // Refresh is admin-only and only surfaces on the Eric / Liz tabs (where
  // NDS re-pull actually matters).
  if (isAdmin && isPartnerTab) {
    toolbarActions.push({
      icon: <RefreshIcon />,
      title: refreshing ? "Refreshing…" : "Refresh",
      handler: () => setRefreshConfirmOpen(true),
      disabled: refreshing,
    });
  }
  // ATM tab gets its own Refresh + Save routed through the ATM hook, so the
  // user sees the same Refresh / Save contextual-menu pattern as Eric / Liz.
  if (isAdmin && tab === "atm") {
    toolbarActions.push({
      icon: <RefreshIcon />,
      title: atmData.refreshing ? "Refreshing…" : "Refresh",
      handler: () => setAtmRefreshConfirmOpen(true),
      disabled: atmData.refreshing || atmData.saving,
    });
    toolbarActions.push({
      icon: <SaveIcon />,
      title: atmData.saving ? "Saving…" : "Save",
      handler: atmData.saveWeek,
      disabled: atmData.saving || atmData.refreshing,
    });
  }
  // Delivery tab surfaces a "Download PDF" action in the contextual menu.
  if (tab === "delivery") {
    toolbarActions.push({
      icon: <GetAppIcon />,
      title: "Download PDF",
      handler: () => downloadDeliveryPdf({ locations: liveLocations, week, year }),
    });
  }
  // Edit/Save toolbar actions only surface for admins. Non-admins see the
  // same tabs in view-only mode.
  if (isPartnerTab && isAdmin) {
    if (isPartnerEditing) {
      toolbarActions.push({
        icon: <SaveIcon />,
        title: saving ? "Saving…" : "Save",
        handler: handleSave,
        disabled: saving,
      });
    }
    toolbarActions.push({
      icon: <EditIcon />,
      title: isPartnerEditing ? "Done editing" : "Edit",
      handler: () => setEditing((e) => ({ ...e, [tab]: !e[tab] })),
    });
  }

  return (
    <>
      <PTToolbar
        title={`${week.id} · ${week.label}, ${year}`}
        onBack={() => nav("weeks", { year, month, mode: "weekly" })}
        actions={toolbarActions}
      />
      <div className="pt-content">
      <div className="pt-subtabs">
        {WEEKLY_TABS.map((t) => (
          <button
            key={t.id}
            className={`pt-subtab ${tab === t.id ? "is-active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="pt-error">{error}</div>}
      {loading && <Spinner label="Loading week…" />}

      {!loading && (
        <>
          {tab === "home" && (
            <HomeTab locations={liveLocations} />
          )}
          {tab === "locations" && (
            <LocationsTab locations={liveLocations} />
          )}
          {tab === "eric" && (
            <EricTab
              locations={liveLocations}
              additions={ericAdditions}
              subtractions={ericSubtractions}
              setAdditions={setEricAdditions}
              setSubtractions={setEricSubtractions}
              atmCashUsed={ericAtmUsed}
              onAtmCashUsedChange={setEricAtmUsed}
              editing={editing.eric}
              saveNote={saveNote}
            />
          )}
          {tab === "liz" && (
            <LizTab
              locations={liveLocations}
              additions={lizAdditions}
              subtractions={lizSubtractions}
              setAdditions={setLizAdditions}
              setSubtractions={setLizSubtractions}
              cashInHand={lizCashInHand}
              onCashInHandChange={setLizCashInHand}
              ericAdditions={ericAdditions}
              ericSubtractions={ericSubtractions}
              editing={editing.liz}
              saveNote={saveNote}
            />
          )}
          {tab === "atm" && (
            <ATMContent
              data={atmData}
              isAdmin={isAdmin}
              hideInlineActions
            />
          )}
          {tab === "delivery" && (
            <DeliveryTab locations={liveLocations} />
          )}
        </>
      )}
      </div>

      <ConfirmDialog
        open={refreshConfirmOpen}
        title="Refresh Week Data?"
        message="This will re-pull the latest figures from the location summary and overwrite every NDS row on Eric and Liz. Your manual entries are preserved."
        confirmLabel={refreshing ? "Refreshing…" : "Refresh"}
        cancelLabel="Cancel"
        busy={refreshing}
        onClose={() => setRefreshConfirmOpen(false)}
        onConfirm={() => {
          setRefreshConfirmOpen(false);
          handleRefresh();
        }}
      />

      <ConfirmDialog
        open={atmRefreshConfirmOpen}
        title="Refresh ATM Records"
        message="This will re-pull every row from the Manager Summary. Any in-progress edits on auto rows will be overwritten; manual rows and mid-week advances are preserved."
        confirmLabel={atmData.refreshing ? "Refreshing…" : "Refresh"}
        cancelLabel="Cancel"
        busy={atmData.refreshing}
        onClose={() => setAtmRefreshConfirmOpen(false)}
        onConfirm={() => {
          setAtmRefreshConfirmOpen(false);
          atmData.refresh();
        }}
      />
    </>
  );
}

// Derive Eric/Liz NDS buckets plus aggregate side-values from a locationSummary response.
// Every item is stamped with a `category` so the per-partner UI can render sections
// (Positive, ATM Negative, ATM Advances, Zelles & Deductions for Liz; Positive,
// Deductions, ATM Advance for Eric) without re-inspecting the description each render.
function deriveNdsBuckets(sessions) {
  const ericAdds = [];
  const ericSubs = [];
  const lizAdds = [];
  const lizSubs = [];
  let totalCashInHand = 0;
  let totalAtmReceipts = 0;
  let totalOwesAmount = 0;

  const mk = (it) => ({
    _id: newItemId(),
    description: it.description,
    amount: Number(it.amount) || 0,
    locationName: it.locationName || "",
    isManual: false,
    category: it.category,
  });

  for (const s of sessions || []) {
    const ms = s.managerSummary || {};
    const loc = s.locationName;
    const managerName = (ms.managerName || "Unknown").trim();
    const delivery = ms.delivery || {};

    // Aggregate side-values used by the Eric/Liz final lines
    totalCashInHand += Number(delivery.cash) || 0;
    totalAtmReceipts += Number(delivery.atmReceipts) || 0;

    // Zelle -> Liz "Zelles & Deductions"
    if (Number(delivery.managerZelle) > 0) {
      lizSubs.push(mk({ description: "Zelle", amount: delivery.managerZelle, locationName: loc, category: "zelle" }));
    }
    // ATM ADV -> Liz "ATM Advances"
    if (Number(delivery.atmAdv) > 0) {
      lizSubs.push(mk({ description: "ATM ADV", amount: delivery.atmAdv, locationName: loc, category: "atmAdv" }));
    }

    // ATM transactions
    for (const t of ms.atmTransactions || []) {
      const amt = Number(t.amount) || 0;
      if (t.type === "ATM_ADV" || t.type === "ATM_DIDNT_USE") totalOwesAmount += amt;
      if (t.type === "ATM_DIDNT_USE" && amt > 0) {
        const desc = `ATM didn't use (${managerName})`;
        // Eric: goes in his Positive bucket. Liz: goes in her ATM Didn't Use section.
        ericAdds.push(mk({ description: desc, amount: amt, locationName: loc, category: "positive" }));
        lizSubs.push(mk({ description: desc, amount: amt, locationName: loc, category: "atmNeg" }));
      }
    }

    // Adjustments — route by assignedTo; additions = Positive, deductions default to "parts"
    // for Eric and "zelle" for Liz (they were historically small deductions; manual items
    // can be moved via edit).
    const adj = ms.adjustments || {};
    for (const a of adj.additions || []) {
      const tgt = (a.assignedTo || "").trim().toLowerCase();
      const item = mk({ description: a.description, amount: a.amount, locationName: loc, category: "positive" });
      if (tgt === "eric") ericAdds.push(item);
      else if (tgt === "liz") lizAdds.push(item);
    }
    for (const d of adj.deductions || []) {
      const tgt = (d.assignedTo || "").trim().toLowerCase();
      if (tgt === "eric") ericSubs.push(mk({ description: d.description, amount: d.amount, locationName: loc, category: "parts" }));
      else if (tgt === "liz") lizSubs.push(mk({ description: d.description, amount: d.amount, locationName: loc, category: "zelle" }));
    }
    for (const o of ms.otherItems || []) {
      const tgt = (o.assignedTo || "").trim().toLowerCase();
      if (tgt === "eric") ericSubs.push(mk({ description: o.description, amount: o.amount, locationName: loc, category: "parts" }));
      else if (tgt === "liz") lizSubs.push(mk({ description: o.description, amount: o.amount, locationName: loc, category: "zelle" }));
    }
  }

  // SAMM — standing $450 line.
  //   Liz Additions: +$450
  //   Eric Deductions: -$450 (mirror, so Eric tab shows the offset in his Deductions section)
  if (!lizAdds.some((x) => x.description === "SAMM")) {
    lizAdds.push(mk({ description: "SAMM", amount: 450, locationName: "", category: "positive" }));
  }
  if (!ericSubs.some((x) => x.description === "SAMM")) {
    ericSubs.push(mk({ description: "SAMM", amount: 450, locationName: "", category: "parts" }));
  }

  // ATM Used from Profit — Eric ATM Advance.
  // Per GM Profits: ericExtra.atm === sum of delivery.atmReceipts; the final
  // computation deducts it BEFORE totalling, so we render it as a standing NDS
  // line in Eric's ATM Advance section.
  if (totalAtmReceipts > 0 && !ericSubs.some((x) => x.description === "ATM Used from Profit")) {
    ericSubs.push(mk({ description: "ATM Used from Profit", amount: totalAtmReceipts, locationName: "", category: "atmAdv" }));
  }

  return {
    ericAdds, ericSubs, lizAdds, lizSubs,
    totalCashInHand, totalAtmReceipts, totalOwesAmount,
  };
}

// When a saved week is loaded, items may not have `category` set (older data).
// Infer one from the description so the new UI can still slot them into the right section.
function inferCategory(item, bucket /* "ericAdd" | "ericSub" | "lizAdd" | "lizSub" */) {
  const d = (item.description || "").trim().toLowerCase();
  if (bucket === "ericAdd" || bucket === "lizAdd") return "positive";
  if (bucket === "ericSub") {
    if (d === "samm") return "parts";
    if (d.startsWith("atm used") || d.startsWith("atm adv") || d.includes("atm didn't use")) return "atmAdv";
    return "parts";
  }
  // lizSub
  if (d.includes("atm didn't use")) return "atmNeg";
  if (d === "zelle") return "zelle";
  if (d.startsWith("atm adv")) return "atmAdv";
  return "zelle";
}

/* ─── HomeTab ───────────────────────────────────────────────── */

function HomeTab({ locations }) {
  const totalProfit = sum(locations, (l) => l.netProfit);
  const active = locations.filter((l) => (Number(l.netProfit) || 0) > 0).length;
  const atmTotal = sum(locations, (l) => l.atm?.cash || 0);

  const partnerTotal = (partnerName) => {
    let total = 0;
    for (const l of locations) {
      for (const inv of l.investors || []) {
        if (inv.name === partnerName) total += computeShare(l, inv);
      }
    }
    return total;
  };
  const liz = partnerTotal("Liz");
  const eric = partnerTotal("Eric");

  const top5 = [...locations]
    .filter((l) => (Number(l.netProfit) || 0) > 0)
    .sort((a, b) => (Number(b.netProfit) || 0) - (Number(a.netProfit) || 0))
    .slice(0, 5);
  const max = top5[0]?.netProfit || 1;

  return (
    <>
      <div className="pt-hero">
        <div className="lb">Total Net Profit</div>
        <div className="big">{money2(totalProfit)}</div>
        <div className="stats">
          <div className="stat">
            <div>
              <div className="k">Active</div>
              <div className="v">
                {active}<span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>/{locations.length}</span>
              </div>
            </div>
          </div>
          <div className="sep" />
          <div className="stat">
            <div>
              <div className="k">ATM Total</div>
              <div className="v">{money0(atmTotal)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-split">
        <div className="card liz">
          <div className="nm">Liz</div>
          <div className="val">{money2(liz)}</div>
          <div className="sub">Base earnings</div>
        </div>
        <div className="card eric">
          <div className="nm">Eric</div>
          <div className="val">{money2(eric)}</div>
          <div className="sub">Base earnings</div>
        </div>
      </div>

      <div className="pt-card">
        <div className="pt-lb"><span role="img" aria-label="trophy">🏆</span> Top 5 Locations</div>
        {top5.length === 0 && <div style={{ color: "var(--pt-muted-2)", fontSize: 12 }}>No profitable locations this week.</div>}
        {top5.map((l, i) => (
          <div key={l.name} style={{ marginBottom: i < top5.length - 1 ? 10 : 0 }}>
            <div className="pt-toprow">
              <span className="nm">{i + 1}. {l.name}</span>
              <span className="v">{money2(l.netProfit)}</span>
            </div>
            <div className="pt-bar">
              <span style={{ width: `${(l.netProfit / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ─── LocationsTab ──────────────────────────────────────────── */

function LocationsTab({ locations }) {
  const [expanded, setExpanded] = useState(null);
  const sorted = [...locations].sort(
    (a, b) => (Number(b.netProfit) || 0) - (Number(a.netProfit) || 0),
  );

  return (
    <>
      <div className="pt-lb" style={{marginTop:'8px'}}>All Locations</div>
      {sorted.length === 0 && (
        <div className="pt-empty">No locations to display.</div>
      )}
      {sorted.map((loc) => {
        const isOpen = expanded === loc.locationId;
        const hasProfit = (Number(loc.netProfit) || 0) > 0;
        const code = (loc.name || "").replace(/[^A-Z0-9]/gi, "").slice(0, 3).toUpperCase();
        return (
          <div key={loc.locationId} className="pt-loc">
            <button
              className="pt-loc-head"
              onClick={() => setExpanded(isOpen ? null : loc.locationId)}
            >
              <div className={`pt-loc-badge ${hasProfit ? "active" : ""}`}>{code}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{loc.name}</div>
                <div style={{ color: "var(--pt-muted-2)", fontSize: 11, marginTop: 1 }}>
                  Profit:{" "}
                  <span style={{ color: hasProfit ? "var(--pt-green)" : "var(--pt-muted-2)", fontWeight: 700 }}>
                    {money2(loc.netProfit || 0)}
                  </span>
                  {(loc.atm?.cash || 0) > 0 && (
                    <span style={{ color: "var(--pt-orange)", marginLeft: 8 }}>
                      ATM {money0(loc.atm.cash)}
                    </span>
                  )}
                </div>
              </div>
              <span style={{ color: "var(--pt-muted-2)", fontSize: 13, transform: isOpen ? "rotate(180deg)" : "rotate(0)" }}>
                ▼
              </span>
            </button>
            {isOpen && (
              <div className="pt-loc-body">
                {(loc.investors || []).filter((i) => i.isManager).map((m) => (
                  <div key={m.investorId} className="pt-mgr">
                    <span>Manager <b>{m.name}</b> ({m.percentage}%)</span>
                    <b>{money2(computeShare(loc, m))}</b>
                  </div>
                ))}
                <div className="pt-pvsplit">
                  {(loc.investors || []).filter((i) => !i.isManager).slice(0, 2).map((inv) => {
                    const isLiz = inv.name === "Liz";
                    return (
                      <div key={inv.investorId} className={`card ${isLiz ? "liz" : "eric"}`}>
                        <div className="lb">{inv.name}</div>
                        <div className="val">{money2(computeShare(loc, inv))}</div>
                        <div className="pct">{inv.percentage}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

/* ─── Shared partner helpers ───────────────────────────────── */

// Classify a description as one of the collapse buckets used by the Positive
// CombinableSectionCard.
//   donations  -> any description containing "donation" / "donations"
//   sams       -> any description containing the standalone word "sam", "sams" or "sam's"
//   atmDidntUse -> any description containing "atm didn't use" (with or without apostrophe)
//   system     -> any description containing the standalone word "system"
// SAMM (the $450 monthly line) is explicitly excluded because its extra 'm' breaks the word boundary.
function collapseBucket(desc) {
  const s = (desc || "").toLowerCase().trim();
  if (/\bdonations?\b/.test(s)) return "donations";
  if (/\bsam(?:'s|s)?\b/.test(s)) return "sams";
  if (/atm\s*didn'?t\s*use/.test(s)) return "atmDidntUse";
  if (/\bsystem\b/.test(s)) return "system";
  return null;
}

function EarningsByLocation({ partnerName, accent, locations }) {
  const earnings = useMemo(() => {
    return locations
      .map((l) => {
        const inv = (l.investors || []).find((i) => i.name === partnerName);
        if (!inv) return null;
        return {
          description: `${l.name} (${inv.percentage}%)`,
          amount: computeShare(l, inv),
          isManual: false,
          locationName: l.name,
          _id: `earn_${l.locationId}`,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.amount - a.amount)
      .map((item, idx) => ({
        ...item,
        description: `${idx + 1}. ${item.description}`,
      }));
  }, [locations, partnerName]);

  return {
    node: (
      <SectionCard
        title="📍 Earnings by Location"
        color={accent}
        items={earnings}
        sign="+"
      />
    ),
    baseEarnings: sum(earnings, (x) => x.amount),
  };
}

/* ─── Eric tab ─────────────────────────────────────────────── */

function EricTab({
  locations, additions, subtractions,
  setAdditions, setSubtractions,
  atmCashUsed, onAtmCashUsedChange,
  editing, saveNote,
}) {
  const accent = "var(--pt-green)";
  const bg = "var(--pt-eric-bg)";
  const [breakdown, setBreakdown] = useState(null); // { title, items, sign }

  const earnings = EarningsByLocation({ partnerName: "Eric", accent, locations });
  const baseEarnings = earnings.baseEarnings;

  // additions is a single list; everything routes to Positive for Eric.
  // subtractions carries category = "parts" | "atmAdv".
  const additionsDisplay = useMemo(
    () =>
      additions.map((it) =>
        it.isManual || !it.locationName
          ? it
          : { ...it, description: `${it.description} (${it.locationName})` }
      ),
    [additions]
  );
  const partsItems = subtractions.filter((x) => (x.category || "parts") === "parts");
  const atmAdvItems = subtractions.filter((x) => x.category === "atmAdv");

  const addTotal = sum(additions, (x) => x.amount);
  const partsTotal = sum(partsItems, (x) => x.amount);
  const atmAdvTotal = sum(atmAdvItems, (x) => x.amount);
  const atmCashUsedNum = Number(atmCashUsed) || 0;
  const atmCombined = atmAdvTotal + atmCashUsedNum;

  // Split atmCashUsedNum (= ATM_ADV + ATM_DIDNT_USE) into its two parts using
  // the live per-location session sums. These come from
  // `liveLocations[].atm.{adv,didntUse}` populated in WeeklyDetail.
  const liveAtmAdvSum = sum(locations, (l) => Number(l?.atm?.adv) || 0);
  const liveAtmDidntUseSum = sum(locations, (l) => Number(l?.atm?.didntUse) || 0);
  const liveOwesTotal = liveAtmAdvSum + liveAtmDidntUseSum;
  // If the user has manually edited atmCashUsedNum so it diverges from live
  // sessions, scale the two parts so they still add up to atmCashUsedNum.
  let atmAdvPart = liveAtmAdvSum;
  let atmDidntUsePart = liveAtmDidntUseSum;
  if (liveOwesTotal > 0 && Math.abs(liveOwesTotal - atmCashUsedNum) > 0.005) {
    const ratio = atmCashUsedNum / liveOwesTotal;
    atmAdvPart = liveAtmAdvSum * ratio;
    atmDidntUsePart = liveAtmDidntUseSum * ratio;
  } else if (liveOwesTotal === 0) {
    atmAdvPart = 0;
    atmDidntUsePart = atmCashUsedNum;
  }
  const totalBeforeDeductions = baseEarnings + addTotal;
  const totalDeductions = partsTotal + atmCombined;
  const lizOwesEric = totalBeforeDeductions - totalDeductions;
  const isNeg = lizOwesEric < 0;
  const finalAbs = Math.abs(lizOwesEric);

  // ----- edit callbacks keyed by full-array index ------------------------
  const addToBucket = (setter, category) => (it) =>
    setter((arr) => [...arr, { ...it, category, _id: newItemId() }]);
  const removeFrom = (setter, filter) => (idxWithinFilter) =>
    setter((arr) => {
      const targets = arr
        .map((it, i) => ({ it, i }))
        .filter(({ it }) => filter(it));
      const target = targets[idxWithinFilter];
      return target ? arr.filter((_, i) => i !== target.i) : arr;
    });
  const editIn = (setter, filter) => (idxWithinFilter, patch) =>
    setter((arr) => {
      const targets = arr
        .map((it, i) => ({ it, i }))
        .filter(({ it }) => filter(it));
      const target = targets[idxWithinFilter];
      if (!target) return arr;
      return arr.map((it, i) => (i === target.i ? { ...it, ...patch } : it));
    });

  return (
    <>
      <div className="pt-lb" style={{ color: accent, marginBottom: 10, marginTop:8 }}>
        Eric's Weekly Summary {editing && <span style={{ color: "var(--pt-teal-txt)", marginLeft: 6 }}>— editing</span>}
      </div>
      {saveNote && (
        <div className={saveNote.kind === "ok" ? "pt-card" : "pt-error"} style={{ padding: "8px 12px", marginBottom: 10, color: saveNote.kind === "ok" ? "var(--pt-green)" : undefined }}>
          {saveNote.text}
        </div>
      )}

      {earnings.node}

      <div className="pt-card" style={{ background: bg }}>
        <div className="pt-lb">Total Base Earnings</div>
        <div className="pt-bg-num" style={{ color: accent }}>{money2(baseEarnings)}</div>
      </div>

      <CombinableSectionCard
        title="➕ Positive"
        color="var(--pt-green)"
        items={additionsDisplay}
        sign="+"
        editable={editing}
        onAdd={addToBucket(setAdditions, "positive")}
        onRemove={(idx) => setAdditions((a) => a.filter((_, i) => i !== idx))}
        onEditItem={(idx, patch) => setAdditions((a) => a.map((x, i) => (i === idx ? { ...x, ...patch } : x)))}
        onOpenBreakdown={setBreakdown}
      />

      <div className="pt-card" style={{ background: bg }}>
        <div className="pt-lb">Total Before Deductions (A)</div>
        <div style={{ fontSize: 11, color: "var(--pt-muted)", marginTop: 2, marginBottom: 4 }}>
          Base Earnings + Positive Adjustments
        </div>
        <div className="pt-bg-num" style={{ color: "var(--pt-green)" }}>{money2(totalBeforeDeductions)}</div>
      </div>

      <SectionCard
        title="➖ Deductions"
        color="var(--pt-red)"
        items={partsItems}
        sign="-"
        totalOverride={totalDeductions}
        totalLabel="Total Deductions"
        editable={editing}
        onAdd={addToBucket(setSubtractions, "parts")}
        onRemove={removeFrom(setSubtractions, (x) => (x.category || "parts") === "parts")}
        onEditItem={editIn(setSubtractions, (x) => (x.category || "parts") === "parts")}
      >
        {editing ? (
          <>
            <div className="pt-sec-row">
              <span className="l">ATM</span>
              <span className="a" style={{ color: "var(--pt-red)" }}>
                − {money2(atmCombined)}
              </span>
            </div>
            <div className="pt-edit-item">
              <div style={{ flex: 1, color: "var(--pt-muted)" }}>ATM Cash Used This Week</div>
              <input
                className="amount"
                type="number"
                value={atmCashUsedNum === 0 ? "" : atmCashUsedNum}
                placeholder="0"
                onChange={(e) => onAtmCashUsedChange(e.target.value === "" ? 0 : Number(e.target.value))}
              />
            </div>
          </>
        ) : (
          <div
            className="pt-sec-row"
            onClick={() => {
              const canBreakdown = liveOwesTotal > 0;
              const ratio = canBreakdown ? atmCashUsedNum / liveOwesTotal : 0;

              const advItems = [];
              const didntUseItems = [];
              const usedFromProfitItems = [];

              if (canBreakdown) {
                for (const loc of locations) {
                  const amt = (Number(loc?.atm?.adv) || 0) * ratio;
                  if (amt > 0) {
                    advItems.push({
                      _id: `atm_adv_${loc.name}`,
                      description: loc.name,
                      amount: amt,
                      isManual: false,
                    });
                  }
                }
                for (const loc of locations) {
                  const amt = (Number(loc?.atm?.didntUse) || 0) * ratio;
                  if (amt > 0) {
                    didntUseItems.push({
                      _id: `atm_didnt_use_${loc.name}`,
                      description: loc.name,
                      amount: amt,
                      isManual: false,
                    });
                  }
                }
              } else {
                if (atmAdvPart > 0) {
                  advItems.push({
                    _id: "atm_adv_total",
                    description: "Total",
                    amount: atmAdvPart,
                    isManual: false,
                  });
                }
                if (atmDidntUsePart > 0) {
                  didntUseItems.push({
                    _id: "atm_didnt_use_total",
                    description: "Total",
                    amount: atmDidntUsePart,
                    isManual: false,
                  });
                }
              }

              const hasAutoUsedFromProfit = atmAdvItems.some(
                (x) => !x.isManual && x.description === "ATM Used from Profit"
              );
              if (hasAutoUsedFromProfit) {
                for (const loc of locations) {
                  const amt = Number(loc?.delivery?.atmReceipts) || 0;
                  if (amt > 0) {
                    usedFromProfitItems.push({
                      _id: `atm_used_from_profit_${loc.name}`,
                      description: loc.name,
                      amount: amt,
                      isManual: false,
                    });
                  }
                }
              }

              const manualItems = atmAdvItems.filter((x) => x.isManual);

              const groups = [];
              if (advItems.length > 0) groups.push({ id: "adv", title: "ATM ADV", items: advItems });
              if (didntUseItems.length > 0) groups.push({ id: "didntUse", title: "ATM didn't use", items: didntUseItems });
              if (usedFromProfitItems.length > 0) groups.push({ id: "usedFromProfit", title: "ATM Used from Profit", items: usedFromProfitItems });
              if (manualItems.length > 0) groups.push({ id: "manual", title: "Other", items: manualItems });

              setBreakdown({
                title: "ATM breakdown",
                groups,
                sign: "-",
              });
            }}
            style={{ cursor: "pointer" }}
          >
            <span className="l">
              ATM
              <span style={{ color: "var(--pt-muted)", fontSize: 10, marginLeft: 6, textDecoration: "underline" }}>
                view breakdown
              </span>
            </span>
            <span className="a" style={{ color: "var(--pt-red)" }}>
              − {money2(atmCombined)}
            </span>
          </div>
        )}
      </SectionCard>

      <div className={`pt-final ${isNeg ? "neg" : "pos"}`}>
        <div className="lb">Net Total (Final)</div>
        <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, marginTop: 2 }}>
          Total Before Deductions − Total Deductions
        </div>
        <div className="big">{isNeg ? "−" : ""}{money2(finalAbs)}</div>
        {isNeg ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, color: "#fff" }}>
            <ErrorOutlineIcon style={{ fontSize: 18 }} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              Eric owes Liz {money2(finalAbs)}
            </span>
          </div>
        ) : (
          <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: "#fff" }}>
            Liz owes Eric {money2(finalAbs)}
          </div>
        )}
      </div>

      <BreakdownDialog
        open={!!breakdown}
        title={breakdown?.title || ""}
        items={breakdown?.items || []}
        groups={breakdown?.groups}
        sign={breakdown?.sign || "+"}
        onClose={() => setBreakdown(null)}
      />
    </>
  );
}

/* ─── Liz tab ──────────────────────────────────────────────── */

function LizTab({
  locations, additions, subtractions,
  setAdditions, setSubtractions,
  cashInHand, onCashInHandChange,
  ericAdditions = [], ericSubtractions = [],
  editing, saveNote,
}) {
  const accent = "var(--pt-blue)";
  const bg = "var(--pt-liz-bg)";
  const [breakdown, setBreakdown] = useState(null);

  const earnings = EarningsByLocation({ partnerName: "Liz", accent, locations });
  const baseEarnings = earnings.baseEarnings;

  // For display only: append "(locationName)" to non-manual additions so
  // each NDS-sourced row shows where it came from. Manual rows keep their
  // own description verbatim. Underlying `additions` (used for save) is
  // untouched.
  const additionsDisplay = useMemo(
    () =>
      additions.map((it) =>
        it.isManual || !it.locationName
          ? it
          : { ...it, description: `${it.description} (${it.locationName})` }
      ),
    [additions]
  );

  // subtractions carries category = "atmNeg" | "atmAdv" | "zelle".
  // A manual entry defaults to "atmNeg". The "ATM Didn't Use" section also
  // absorbs any legacy entries saved with category "zelle" whose description
  // matches the ATM didn't use pattern (collapseBucket -> "atmDidntUse"), so
  // pre-existing saved weeks render correctly without a migration.
  const isAtmDidntUse = (it) => collapseBucket(it.description) === "atmDidntUse";
  const atmNegItems = subtractions.filter(
    (x) => (x.category || "atmNeg") === "atmNeg" || (x.category === "zelle" && isAtmDidntUse(x)),
  );
  const atmAdvItems = subtractions.filter((x) => x.category === "atmAdv");
  const zelleItems = subtractions.filter((x) => x.category === "zelle" && !isAtmDidntUse(x));

  const addTotal = sum(additions, (x) => x.amount);
  const atmNegTotal = sum(atmNegItems, (x) => x.amount);
  const atmAdvTotal = sum(atmAdvItems, (x) => x.amount);
  const zelleTotal = sum(zelleItems, (x) => x.amount);
  // Liz's own subtotal (base + additions − subtractions). Matches
  // LizAccounting's `subtractionsTotal` used as "Liz Profit".
  const lizProfit = baseEarnings + addTotal - atmNegTotal - atmAdvTotal - zelleTotal;

  // Eric's "Profit" = his Sub-Total (pre-owes). Matches LizAccounting.ericSubTotal.
  const ericBase = sum(
    locations.filter((l) => (Number(l.netProfit) || 0) > 0),
    (l) => {
      const inv = (l.investors || []).find((i) => i.name === "Eric");
      return inv ? computeShare(l, inv) : 0;
    },
  );
  const ericAddTotal = sum(ericAdditions, (x) => x.amount);
  const ericPartsTotal = sum(
    ericSubtractions.filter((x) => (x.category || "parts") === "parts"),
    (x) => x.amount,
  );
  const ericAtmAdvTotal = sum(
    ericSubtractions.filter((x) => x.category === "atmAdv"),
    (x) => x.amount,
  );
  const ericProfit = ericBase + ericAddTotal - ericPartsTotal - ericAtmAdvTotal;

  const totalCash = lizProfit + ericProfit;
  const shortOver = (Number(cashInHand) || 0) - totalCash;

  const addToBucket = (setter, category) => (it) =>
    setter((arr) => [...arr, { ...it, category, _id: newItemId() }]);
  const removeFrom = (setter, filter) => (idxWithinFilter) =>
    setter((arr) => {
      const targets = arr.map((it, i) => ({ it, i })).filter(({ it }) => filter(it));
      const target = targets[idxWithinFilter];
      return target ? arr.filter((_, i) => i !== target.i) : arr;
    });
  const editIn = (setter, filter) => (idxWithinFilter, patch) =>
    setter((arr) => {
      const targets = arr.map((it, i) => ({ it, i })).filter(({ it }) => filter(it));
      const target = targets[idxWithinFilter];
      if (!target) return arr;
      return arr.map((it, i) => (i === target.i ? { ...it, ...patch } : it));
    });

  return (
    <>
      <div
        className="pt-lb"
        style={{ color: accent, marginBottom: 10, marginTop: 8 }}
      >
        Liz's Weekly Summary{' '}
        {editing && (
          <span style={{ color: 'var(--pt-teal-txt)', marginLeft: 6 }}>
            — editing
          </span>
        )}
      </div>
      {saveNote && (
        <div
          className={saveNote.kind === 'ok' ? 'pt-card' : 'pt-error'}
          style={{
            padding: '8px 12px',
            marginBottom: 10,
            color: saveNote.kind === 'ok' ? 'var(--pt-green)' : undefined,
          }}
        >
          {saveNote.text}
        </div>
      )}

      {earnings.node}

      <div className="pt-card" style={{ background: bg }}>
        <div className="pt-lb">Total Base Earnings</div>
        <div className="pt-bg-num" style={{ color: accent }}>
          {money2(baseEarnings)}
        </div>
      </div>

      <CombinableSectionCard
        title="➕ Positive"
        color="var(--pt-green)"
        items={additionsDisplay}
        sign="+"
        editable={editing}
        onAdd={addToBucket(setAdditions, 'positive')}
        onRemove={(idx) => setAdditions((a) => a.filter((_, i) => i !== idx))}
        onEditItem={(idx, patch) =>
          setAdditions((a) =>
            a.map((x, i) => (i === idx ? { ...x, ...patch } : x))
          )
        }
        onOpenBreakdown={setBreakdown}
      />

      <div className="pt-card" style={{ background: bg }}>
        <div className="pt-lb">Total Before Deductions (A)</div>
        <div style={{ fontSize: 11, color: 'var(--pt-muted)', marginTop: 2, marginBottom: 4 }}>
          Base Earnings + Positive Adjustments
        </div>
        <div className="pt-bg-num" style={{ color: accent }}>
          {money2(baseEarnings + addTotal)}
        </div>
      </div>

      <CombinableSectionCard
        title="🏧 ATM DIDN'T USE"
        color="var(--pt-red)"
        items={atmNegItems}
        sign="-"
        editable={editing}
        onAdd={addToBucket(setSubtractions, 'atmNeg')}
        onRemove={removeFrom(
          setSubtractions,
          (x) => (x.category || 'atmNeg') === 'atmNeg' || (x.category === 'zelle' && isAtmDidntUse(x)),
        )}
        onEditItem={editIn(
          setSubtractions,
          (x) => (x.category || 'atmNeg') === 'atmNeg' || (x.category === 'zelle' && isAtmDidntUse(x)),
        )}
        onOpenBreakdown={setBreakdown}
      />

      <CombinableSectionCard
        title="🏧 ATM Advances"
        color="var(--pt-orange)"
        items={atmAdvItems}
        sign="-"
        editable={editing}
        onAdd={addToBucket(setSubtractions, 'atmAdv')}
        onRemove={removeFrom(setSubtractions, (x) => x.category === 'atmAdv')}
        onEditItem={editIn(setSubtractions, (x) => x.category === 'atmAdv')}
        onOpenBreakdown={setBreakdown}
        collapseGroups={LIZ_ATM_ADV_GROUPS}
      />

      <CombinableSectionCard
        title="🏦 Zelles & Deductions"
        color="var(--pt-purple)"
        items={zelleItems}
        sign="-"
        editable={editing}
        onAdd={addToBucket(setSubtractions, 'zelle')}
        onRemove={removeFrom(setSubtractions, (x) => x.category === 'zelle')}
        onEditItem={editIn(setSubtractions, (x) => x.category === 'zelle')}
        onOpenBreakdown={setBreakdown}
        collapseGroups={LIZ_ZELLE_GROUPS}
      />

      {/* Weekly summary — mirrors the block at the end of GM Profits Liz panel */}
      <LizWeeklySummary
        lizProfit={lizProfit}
        ericProfit={ericProfit}
        cashExpected={totalCash}
        cashInHand={cashInHand}
        onCashInHandChange={onCashInHandChange}
        shortOver={shortOver}
        editing={editing}
      />

      <BreakdownDialog
        open={!!breakdown}
        title={breakdown?.title || ''}
        items={breakdown?.items || []}
        groups={breakdown?.groups}
        sign={breakdown?.sign || '+'}
        onClose={() => setBreakdown(null)}
      />
    </>
  );
}

// Default collapse groups for the Positive card (shared by Eric and Liz).
const DEFAULT_COLLAPSE_GROUPS = [
  {
    id: "donations",
    title: "Donations",
    breakdownTitle: "Donations by location",
    match: (it) => collapseBucket(it.description) === "donations",
  },
  {
    id: "sams",
    title: "Sam's delivery",
    breakdownTitle: "Sam's delivery by location",
    match: (it) => collapseBucket(it.description) === "sams",
  },
  {
    id: "atmDidntUse",
    title: "ATM didn't use",
    breakdownTitle: "ATM didn't use by location",
    match: (it) => collapseBucket(it.description) === "atmDidntUse",
  },
  {
    id: "system",
    title: "System",
    breakdownTitle: "System by location",
    match: (it) => collapseBucket(it.description) === "system",
  },
];

// A SectionCard variant that collapses NDS-sourced entries into one clickable
// row per group (in view mode). Click opens a BreakdownDialog with per-location
// rows. `collapseGroups` is an array of { id, title, breakdownTitle, match }
// entries; defaults to Donations + Sam's delivery for back-compat.
function CombinableSectionCard({
  title, color, items, sign, editable, onAdd, onRemove, onEditItem, onOpenBreakdown,
  collapseGroups = DEFAULT_COLLAPSE_GROUPS,
}) {
  const bucketed = {};
  const rest = [];
  const restIndex = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const g = collapseGroups.find((grp) => grp.match(it));
    if (g) {
      if (!bucketed[g.id]) bucketed[g.id] = [];
      bucketed[g.id].push(it);
    } else {
      rest.push(it);
      restIndex.push(i);
    }
  }

  const viewItems = [...rest];
  for (const g of collapseGroups) {
    const list = bucketed[g.id];
    if (list && list.length > 0) {
      viewItems.push({
        _id: `__collapsed_${g.id}__`,
        description: g.title,
        amount: list.reduce((a, b) => a + (Number(b.amount) || 0), 0),
        isManual: false,
        _breakdown: list,
        _breakdownTitle: g.breakdownTitle || g.title,
      });
    }
  }

  const totalOverride = items.reduce((a, b) => a + (Number(b.amount) || 0), 0);

  return (
    <SectionCard
      title={title}
      color={color}
      items={viewItems}
      sign={sign}
      totalOverride={totalOverride}
      editable={editable}
      onAdd={onAdd}
      onRemove={
        editable && onRemove
          ? (idxInView) => {
              if (idxInView < restIndex.length) onRemove(restIndex[idxInView]);
            }
          : undefined
      }
      onEditItem={
        editable && onEditItem
          ? (idxInView, patch) => {
              if (idxInView < restIndex.length) onEditItem(restIndex[idxInView], patch);
            }
          : undefined
      }
      renderRowClick={(item) => {
        if (item._breakdown) {
          onOpenBreakdown({
            title: item._breakdownTitle || item.description,
            items: item._breakdown,
            sign,
          });
        }
      }}
    />
  );
}

// Matches the auto-generated "ATM ADV" rows pushed by buildBucketsFromDeliveries.
// Manual rows (user-added in edit mode) keep their own descriptions and stay
// individually visible.
const LIZ_ATM_ADV_GROUPS = [
  {
    id: "atmAdv",
    title: "ATM ADV",
    breakdownTitle: "ATM ADV by location",
    match: (it) => (it.description || "").trim().toLowerCase() === "atm adv",
  },
];

const LIZ_ZELLE_GROUPS = [
  {
    id: "zelle",
    title: "Zelle",
    breakdownTitle: "Zelle by location",
    match: (it) => (it.description || "").trim().toLowerCase() === "zelle",
  },
];

function LizWeeklySummary({
  lizProfit, ericProfit, cashExpected, cashInHand, onCashInHandChange, shortOver, editing,
}) {
  const isOver = shortOver >= 0;

  return (
    <div
      style={{
        borderRadius: 18,
        overflow: "hidden",
        border: "1px solid var(--pt-border)",
        boxShadow: "var(--pt-shadow-md)",
        marginBottom: 20,
        marginTop: 4,
        background: "var(--pt-card)",
      }}
    >
      {/* Dark navy header */}
      <div
        style={{
          background: "var(--pt-navy)",
          color: "#fff",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(255,255,255,0.65)" }}>
            Weekly Summary
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>Liz · Week close-out</div>
        </div>
      </div>

      {/* Profit breakdown */}
      <div style={{ padding: "10px 20px 4px" }}>
        <SummaryLine label="Liz Profit" value={lizProfit} dotColor="var(--pt-blue)" />
        <SummaryLine label="Eric Profit" value={ericProfit} dotColor="var(--pt-green)" />
      </div>

      {/* Cash Expected row — emphasised */}
      <div
        style={{
          background: "#f8fafc",
          borderTop: "1px solid var(--pt-border)",
          borderBottom: "1px solid var(--pt-border)",
          padding: "14px 20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}
      >
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--pt-muted)" }}>
            Expected
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--pt-text)", marginTop: 2 }}>
            Cash Expected
          </div>
        </div>
        <div
          style={{
            fontSize: 22, fontWeight: 800, letterSpacing: "-0.3px",
            color: cashExpected < 0 ? "var(--pt-red)" : "var(--pt-text)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {cashExpected < 0 ? "−" : ""}{money2(Math.abs(cashExpected))}
        </div>
      </div>

      {/* Cash In Hand — editable when editing, display otherwise */}
      <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--pt-muted)" }}>
            Delivered
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--pt-text)", marginTop: 2 }}>
            Cash In Hand
          </div>
        </div>
        {editing ? (
          <div style={{ position: "relative", width: 180 }}>
            <span
              style={{
                position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                color: "var(--pt-muted)", fontSize: 15, fontWeight: 600,
              }}
            >
              $
            </span>
            <input
              type="number"
              value={cashInHand === 0 || cashInHand === "" ? "" : cashInHand}
              placeholder="0"
              onChange={(e) => onCashInHandChange(e.target.value === "" ? 0 : Number(e.target.value))}
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "10px 12px 10px 26px",
                border: "1.5px solid var(--pt-teal)",
                borderRadius: 10,
                fontSize: 18, fontWeight: 700,
                color: "var(--pt-text)",
                background: "#fff",
                outline: "none", fontFamily: "inherit",
                fontVariantNumeric: "tabular-nums",
                textAlign: "right",
              }}
            />
          </div>
        ) : (
          <div
            style={{
              fontSize: 20, fontWeight: 800,
              color: "var(--pt-green)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {money2(cashInHand)}
          </div>
        )}
      </div>

      {/* Short/Over footer strip */}
      <div
        style={{
          background: isOver ? "#ecfdf5" : "#fef2f2",
          borderTop: `2px solid ${isOver ? "#a7f3d0" : "#fecaca"}`,
          padding: "14px 20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 10, height: 10, borderRadius: 5, flexShrink: 0,
              background: isOver ? "var(--pt-green)" : "var(--pt-red)",
            }}
          />
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", color: isOver ? "#047857" : "#b91c1c" }}>
              {isOver ? "Over by" : "Short by"}
            </div>
            <div style={{ fontSize: 11, color: "var(--pt-muted)", marginTop: 1 }}>
              Cash In Hand − Cash Expected
            </div>
          </div>
        </div>
        <div
          style={{
            fontSize: 22, fontWeight: 900,
            color: isOver ? "#047857" : "#b91c1c",
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            flexShrink: 0,
            marginLeft: 10,
          }}
        >
          {`${isOver ? "+" : "−"}${money2(Math.abs(shortOver))}`}
        </div>
      </div>
    </div>
  );
}

function SummaryLine({ label, value, dotColor }) {
  const v = Number(value) || 0;
  return (
    <div
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 0",
        borderBottom: "1px dashed var(--pt-border)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            width: 8, height: 8, borderRadius: 4,
            background: dotColor || "var(--pt-muted-2)",
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 14, color: "var(--pt-text-2)", fontWeight: 500 }}>
          {label}
        </span>
      </div>
      <span
        style={{
          fontSize: 15, fontWeight: 700,
          color: v < 0 ? "var(--pt-red)" : "var(--pt-text)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {v < 0 ? "−" : ""}{money2(Math.abs(v))}
      </span>
    </div>
  );
}

/* ─── Delivery tab ──────────────────────────────────────────── */

function DeliveryTab({ locations }) {
  const totalCash = sum(locations, (l) => l.delivery?.cash);
  const totalZelle = sum(locations, (l) => l.delivery?.managerZelle);
  const totalR = sum(locations, (l) => l.delivery?.atmReceipts);
  const totalAdv = sum(locations, (l) => l.delivery?.atmAdv);
  const grand = totalCash + totalZelle + totalR + totalAdv;

  return (
    <>
      <div className="pt-delivery-grid">
        <div className="pt-delivery-card green">
          <div className="lb">Total Cash</div>
          <div className="v">{money0(totalCash)}</div>
        </div>
        <div className="pt-delivery-card blue">
          <div className="lb">Total Zelle</div>
          <div className="v">{money0(totalZelle)}</div>
        </div>
        <div className="pt-delivery-card red">
          <div className="lb">ATM Receipts</div>
          <div className="v">{money0(totalR)}</div>
        </div>
        <div className="pt-delivery-card orange">
          <div className="lb">ATM Adv</div>
          <div className="v">{money0(totalAdv)}</div>
        </div>
      </div>

      <div className="pt-card" style={{ borderColor: "rgba(167,139,250,0.3)", background: "rgba(167,139,250,0.08)" }}>
        <div className="pt-lb" style={{ color: "var(--pt-purple)" }}>Total Delivered</div>
        <div className="pt-bg-num" style={{ color: "var(--pt-purple)" }}>{money0(grand)}</div>
      </div>

      <div className="pt-tbl-wrap">
        <div className="pt-tbl-scroll">
          <table className="pt-tbl">
            <thead>
              <tr>
                <th>Location</th><th>Cash</th><th>Zelle</th><th>ATM Rec</th><th>ATM Adv</th><th>Total</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((l) => {
                const d = l.delivery || {};
                const t = (Number(d.cash) || 0) + (Number(d.managerZelle) || 0) + (Number(d.atmReceipts) || 0) + (Number(d.atmAdv) || 0);
                return (
                  <tr key={l.name}>
                    <td>{l.name}</td>
                    <td>{money0(d.cash || 0)}</td>
                    <td>{money0(d.managerZelle || 0)}</td>
                    <td>{money0(d.atmReceipts || 0)}</td>
                    <td>{money0(d.atmAdv || 0)}</td>
                    <td style={{ fontWeight: 800 }}>{money0(t)}</td>
                  </tr>
                );
              })}
              <tr className="total">
                <td>TOTAL</td>
                <td>{money0(totalCash)}</td>
                <td>{money0(totalZelle)}</td>
                <td>{money0(totalR)}</td>
                <td>{money0(totalAdv)}</td>
                <td>{money0(grand)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   ATM views (embedded tab + standalone detail screen)
   ══════════════════════════════════════════════════════════ */

const ATM_TABS = [
  { id: "summary", label: "Summary" },
  { id: "du", label: "Didn't Use" },
  { id: "lw", label: "Last Week" },
  { id: "nw", label: "Next Week" },
];

// Which (year, month, weekNumber) precedes (y, m, wn)? Mirrors ATMReceipt.getPreviousWeek.
function previousWeekOf(year, month1, weekNumber) {
  if (weekNumber > 1) {
    return { year, month: month1, weekNumber: weekNumber - 1 };
  }
  const prevMonth = month1 === 1 ? 12 : month1 - 1;
  const prevYear = month1 === 1 ? year - 1 : year;
  const weeks = weeksInMonth(prevYear, prevMonth);
  if (!weeks.length) return null;
  return { year: prevYear, month: prevMonth, weekNumber: weeks[weeks.length - 1].weekNumber };
}

function useATMData(ctx, api, locationNames) {
  const { year, month, week } = ctx;
  const month1 = MONTHS.indexOf(month) + 1;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState([]);
  const [autoRows, setAutoRows] = useState([]);
  const [lastWeekRows, setLastWeekRows] = useState([]);
  const [manualRows, setManualRows] = useState([]);
  const [duRows, setDuRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!week) return;
    setLoading(true);
    setError(null);

    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);
    const prevWeek = previousWeekOf(year, month1, week.weekNumber);

    const sumTx = (txs, type) =>
      (txs || [])
        .filter((t) => t.type === type)
        .reduce((a, b) => a + (Number(b.amount) || 0), 0);

    // Promise wrappers around the callback-based apiRequest.
    const pCurrent = new Promise((resolve) => {
      api.fetchATMWeek(
        { year, month: month1, weekNumber: week.weekNumber },
        (res) => resolve(res?.week || null),
        () => resolve(null),
      );
    });
    const pPrev = prevWeek
      ? new Promise((resolve) => {
          api.fetchATMWeek(prevWeek, (res) => resolve(res?.week || null), () => resolve(null));
        })
      : Promise.resolve(null);
    const pLive = new Promise((resolve, reject) => {
      api.fetchLocationSummary(
        { fromDate, toDate, locationNames: locationNames || [] },
        (res) => resolve(res),
        (err) => reject(err),
      );
    });

    Promise.all([pCurrent, pPrev, pLive])
      .then(([savedWeek, prevWeekData, liveSummary]) => {
        const sessions = liveSummary?.sessions || [];

        // --- Summary tab: normalise the saved [{location, summary}] shape
        // or derive {name, cash, gm} rows from the live sessions.
        const summaryRows = (() => {
          if (savedWeek?.atmSummary?.length) {
            return savedWeek.atmSummary.map((entry) => {
              if (entry?.summary && Array.isArray(entry.summary.atmTransactions)) {
                const tx = entry.summary.atmTransactions;
                return {
                  name: entry.location || entry.summary.managerName || "",
                  cash: sumTx(tx, "ATM_CASH"),
                  gm: sumTx(tx, "ATM_FROM_PROFIT"),
                };
              }
              return {
                name: entry?.name || entry?.location || "",
                cash: Number(entry?.cash) || 0,
                gm: Number(entry?.gm) || 0,
              };
            });
          }
          return sessions.map((s) => {
            const tx = s.managerSummary?.atmTransactions || [];
            return {
              name: s.locationName,
              cash: sumTx(tx, "ATM_CASH"),
              gm: sumTx(tx, "ATM_FROM_PROFIT"),
            };
          });
        })();
        setSummary(summaryRows);

        // --- Didn't-Use rows: per-session ATM_DIDNT_USE totals (live).
        const du = [];
        for (const s of sessions) {
          const ms = s.managerSummary || {};
          const amt = sumTx(ms.atmTransactions, "ATM_DIDNT_USE");
          if (amt > 0) du.push({ name: ms.managerName || s.locationName, amt });
        }
        setDuRows(du);

        // --- Next Week autoRows: saved if present, else derived fresh by
        // grouping LOCATION_SUMMARY sessions by manager (matches
        // ATMReceipt.getNextWeekAutoRows).
        const buildFreshAutoRows = () => {
          const map = {};
          for (const s of sessions) {
            const ms = s.managerSummary || {};
            const manager = (ms.managerName || "").trim();
            if (!manager || manager === "undefined" || manager === "null") continue;
            if (!map[manager]) {
              map[manager] = {
                id: newItemId(),
                manager,
                eric: 0,
                profit: 0,
                atmDidntUse: 0,
                advEly: 0,
              };
            }
            map[manager].eric += sumTx(ms.atmTransactions, "ATM_CASH");
            map[manager].profit += sumTx(ms.atmTransactions, "ATM_ADV");
            map[manager].atmDidntUse += sumTx(ms.atmTransactions, "ATM_DIDNT_USE");
          }
          return Object.values(map);
        };
        setAutoRows(
          savedWeek?.autoRows?.length
            ? savedWeek.autoRows.map((r) => ({ ...r, id: r.id || newItemId() }))
            : buildFreshAutoRows(),
        );

        // --- manualNextWeekRows: only ever from saved state.
        setManualRows(
          (savedWeek?.manualNextWeekRows || []).map((r) => ({
            ...r,
            id: r.id || newItemId(),
          })),
        );

        // --- Last Week rows: derived from the previous week's autoRows +
        // manualNextWeekRows. atmLastWeek[manager] = Σ (eric + profit +
        // atmDidntUse) for that manager in the previous week. midWeekAdv is
        // preserved from the current week's saved lastWeekRows.
        const prevTotals = {};
        if (prevWeekData) {
          const allPrev = [
            ...(prevWeekData.autoRows || []),
            ...(prevWeekData.manualNextWeekRows || []),
          ];
          for (const r of allPrev) {
            const m = (r.manager || "").trim();
            if (!m || m === "undefined" || m === "null") continue;
            prevTotals[m] =
              (prevTotals[m] || 0) +
              (Number(r.eric) || 0) +
              (Number(r.profit) || 0) +
              (Number(r.atmDidntUse) || 0);
          }
        }
        const savedMid = savedWeek?.lastWeekRows || [];
        const lastWeekBuilt = Object.entries(prevTotals).map(([manager, atmLastWeek]) => {
          const savedRow = savedMid.find(
            (r) => (r.manager || "").trim().toLowerCase() === manager.toLowerCase(),
          );
          const midWeekAdv = Number(savedRow?.midWeekAdv) || 0;
          const total = (Number(atmLastWeek) || 0) + midWeekAdv;
          return {
            manager,
            atmLastWeek: Number(atmLastWeek) || 0,
            midWeekAdv,
            total,
          };
        });
        setLastWeekRows(lastWeekBuilt);

        setLoading(false);
      })
      .catch((err) => {
        setError(typeof err === "string" ? err : "Failed to load ATM data");
        setLoading(false);
      });
  }, [year, month1, week, api, locationNames]);

  const closeEdits = () => {
    setLastWeekRows((arr) => arr.map((r) => ({ ...r, isEditing: false, _original: undefined })));
    setAutoRows((arr) => arr.map((r) => ({ ...r, isEditing: false, _original: undefined })));
    setManualRows((arr) => arr.map((r) => ({ ...r, isEditing: false, _original: undefined })));
  };

  const saveWeek = () => {
    if (!week) return;
    closeEdits();
    setSaving(true);
    setFeedback(null);
    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);
    const payload = {
      year,
      month: month1,
      weekNumber: week.weekNumber,
      fromDate: toISO(fromDate),
      toDate: toISO(toDate),
      atmSummary: [],
      lastWeekRows: lastWeekRows.map((r) => ({
        manager: r.manager,
        atmLastWeek: Number(r.atmLastWeek) || 0,
        midWeekAdv: Number(r.midWeekAdv) || 0,
      })),
      autoRows: autoRows.map((r) => ({
        id: r.id,
        manager: r.manager,
        eric: Number(r.eric) || 0,
        profit: Number(r.profit) || 0,
        advEly: Number(r.advEly) || 0,
        atmDidntUse: Number(r.atmDidntUse) || 0,
        isLocked: !!r.isLocked,
      })),
      manualNextWeekRows: manualRows.map((r) => ({
        id: r.id,
        manager: r.manager,
        eric: Number(r.eric) || 0,
        profit: Number(r.profit) || 0,
        advEly: Number(r.advEly) || 0,
        atmDidntUse: Number(r.atmDidntUse) || 0,
      })),
    };
    api.saveATMWeek(
      payload,
      () => {
        setSaving(false);
        setFeedback({ kind: "ok", text: "Saved" });
        setTimeout(() => setFeedback(null), 2500);
      },
      (err) => {
        setSaving(false);
        setFeedback({ kind: "err", text: typeof err === "string" ? err : "Save failed" });
      },
    );
  };

  const refresh = () => {
    if (!week) return;
    setRefreshing(true);
    setFeedback(null);
    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);
    const sumTx = (txs, type) =>
      (txs || [])
        .filter((t) => t.type === type)
        .reduce((a, b) => a + (Number(b.amount) || 0), 0);

    api.fetchLocationSummary(
      { fromDate, toDate, locationNames: locationNames || [] },
      (res) => {
        const sessions = res?.sessions || [];

        // Rebuild fresh per-manager totals from live sessions.
        const freshMap = {};
        for (const s of sessions) {
          const ms = s.managerSummary || {};
          const manager = (ms.managerName || "").trim();
          if (!manager || manager === "undefined" || manager === "null") continue;
          if (!freshMap[manager]) {
            freshMap[manager] = {
              manager, eric: 0, profit: 0, atmDidntUse: 0, advEly: 0,
            };
          }
          freshMap[manager].eric += sumTx(ms.atmTransactions, "ATM_CASH");
          freshMap[manager].profit += sumTx(ms.atmTransactions, "ATM_ADV");
          freshMap[manager].atmDidntUse += sumTx(ms.atmTransactions, "ATM_DIDNT_USE");
        }

        // Merge into existing autoRows: preserve advEly + rows currently in edit + locked rows.
        setAutoRows((prev) => {
          const merged = prev.map((r) => ({ ...r }));
          for (const fresh of Object.values(freshMap)) {
            if (merged.some((r) => r.manager === fresh.manager && r.isLocked)) continue;
            const idx = merged.findIndex((r) => r.manager === fresh.manager && !r.isEditing);
            if (idx !== -1) {
              merged[idx] = {
                ...merged[idx],
                eric: fresh.eric,
                profit: fresh.profit,
                atmDidntUse: fresh.atmDidntUse,
              };
            } else if (!merged.some((r) => r.manager === fresh.manager)) {
              merged.push({ id: newItemId(), isLocked: false, ...fresh });
            }
          }
          return merged;
        });

        // Also refresh Summary and Didn't Use from live sessions.
        setSummary(
          sessions.map((s) => {
            const tx = s.managerSummary?.atmTransactions || [];
            return {
              name: s.locationName,
              cash: sumTx(tx, "ATM_CASH"),
              gm: sumTx(tx, "ATM_FROM_PROFIT"),
            };
          }),
        );
        const du = [];
        for (const s of sessions) {
          const ms = s.managerSummary || {};
          const amt = sumTx(ms.atmTransactions, "ATM_DIDNT_USE");
          if (amt > 0) du.push({ name: ms.managerName || s.locationName, amt });
        }
        setDuRows(du);

        setRefreshing(false);
        setFeedback({ kind: "ok", text: "Refreshed" });
        setTimeout(() => setFeedback(null), 2500);
      },
      (err) => {
        setRefreshing(false);
        setFeedback({ kind: "err", text: typeof err === "string" ? err : "Refresh failed" });
      },
    );
  };

  return {
    loading, error,
    summary, autoRows, lastWeekRows, manualRows, duRows,
    setAutoRows, setLastWeekRows, setManualRows,
    saveWeek, refresh, saving, refreshing, feedback,
  };
}

function ATMContent({ data, isAdmin, hideInlineActions }) {
  const [tab, setTab] = useState("summary");
  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);
  const {
    loading, error, summary, autoRows, lastWeekRows, manualRows, duRows,
    setAutoRows, setLastWeekRows, setManualRows,
    saveWeek, refresh, saving, refreshing, feedback,
  } = data;
  const canEdit = !!isAdmin;
  const showInlineActions = canEdit && !hideInlineActions;

  // ── Last Week row helpers ───────────────────────────────
  const toggleLastWeekEdit = (manager) =>
    setLastWeekRows((arr) =>
      arr.map((r) => r.manager === manager
        ? { ...r, isEditing: !r.isEditing, _original: r.isEditing ? undefined : { ...r } }
        : r),
    );
  const updateLastWeekMidAdv = (manager, value) => {
    const v = value === "" ? 0 : Number(value) || 0;
    setLastWeekRows((arr) =>
      arr.map((r) => r.manager === manager
        ? { ...r, midWeekAdv: v, total: (Number(r.atmLastWeek) || 0) + v }
        : r),
    );
  };
  const cancelLastWeekEdit = (manager) =>
    setLastWeekRows((arr) =>
      arr.map((r) => r.manager === manager && r._original
        ? { ...r._original, isEditing: false, _original: undefined }
        : r),
    );
  const confirmLastWeekEdit = (manager) => {
    setLastWeekRows((arr) =>
      arr.map((r) => r.manager === manager ? { ...r, isEditing: false, _original: undefined } : r),
    );
    setTimeout(saveWeek, 0);
  };

  // ── Auto row helpers (Next Week) ────────────────────────
  const toggleAutoEdit = (id) =>
    setAutoRows((arr) =>
      arr.map((r) => r.id === id
        ? { ...r, isEditing: !r.isEditing, _original: r.isEditing ? undefined : { ...r } }
        : r),
    );
  const updateAutoField = (id, field, value) => {
    const v = field === "manager" ? value : (value === "" ? 0 : Number(value) || 0);
    setAutoRows((arr) =>
      arr.map((r) => r.id === id ? { ...r, [field]: v } : r),
    );
  };
  const cancelAutoEdit = (id) =>
    setAutoRows((arr) =>
      arr.map((r) => r.id === id && r._original
        ? { ...r._original, isEditing: false, _original: undefined }
        : r),
    );
  const confirmAutoEdit = (id) => {
    setAutoRows((arr) =>
      arr.map((r) => r.id === id ? { ...r, isEditing: false, isLocked: true, _original: undefined } : r),
    );
    setTimeout(saveWeek, 0);
  };
  const toggleAutoLock = (id) => {
    setAutoRows((arr) =>
      arr.map((r) => r.id === id ? { ...r, isLocked: !r.isLocked } : r),
    );
    setTimeout(saveWeek, 0);
  };

  // ── Manual row helpers (Next Week) ──────────────────────
  const addManualRow = () => {
    setManualRows((arr) => [
      ...arr,
      { id: newItemId(), manager: "", eric: 0, profit: 0, atmDidntUse: 0, advEly: 0, isEditing: true },
    ]);
  };
  const updateManualField = (id, field, value) => {
    const v = field === "manager" ? value : (value === "" ? 0 : Number(value) || 0);
    setManualRows((arr) =>
      arr.map((r) => r.id === id ? { ...r, [field]: v } : r),
    );
  };
  const toggleManualEdit = (id) =>
    setManualRows((arr) =>
      arr.map((r) => r.id === id
        ? { ...r, isEditing: !r.isEditing, _original: r.isEditing ? undefined : { ...r } }
        : r),
    );
  const cancelManualEdit = (id) =>
    setManualRows((arr) => {
      const row = arr.find((r) => r.id === id);
      // new row that was never confirmed -> remove it entirely
      if (!row || !row._original) return arr.filter((r) => r.id !== id);
      return arr.map((r) => r.id === id
        ? { ...r._original, isEditing: false, _original: undefined }
        : r);
    });
  const confirmManualEdit = (id) => {
    setManualRows((arr) =>
      arr.map((r) => r.id === id ? { ...r, isEditing: false, _original: undefined } : r),
    );
    setTimeout(saveWeek, 0);
  };
  const removeManualRow = (id) => {
    setManualRows((arr) => arr.filter((r) => r.id !== id));
    setTimeout(saveWeek, 0);
  };

  const sc = sum(summary, (r) => r.cash);
  const sg = sum(summary, (r) => r.gm);
  const sTot = sc + sg;

  const la = sum(lastWeekRows, (r) => r.atmLastWeek);
  const lm = sum(lastWeekRows, (r) => r.midWeekAdv);
  const lTot = la + lm;

  const nextRows = [...autoRows, ...manualRows];
  // Per ATMReceipt source: row.eric = ATM_CASH, row.profit = ATM_ADV, row.atmDidntUse = ATM_DIDNT_USE.
  const nc = sum(nextRows, (r) => r.eric);
  const na = sum(nextRows, (r) => r.profit);
  const nd = sum(nextRows, (r) => r.atmDidntUse);
  const nTot = nc + na + nd;

  const dt = sum(duRows, (r) => r.amt);

  return (
    <>
      {/* Subtabs + action bar — Refresh + Save are admin-only. */}
      <div
        className={`pt-atm-subtabs${hideInlineActions ? " pt-atm-subtabs-embedded" : ""}`}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 10, marginBottom: 6,
        }}
      >
        <div className="pt-subtabs" style={{ padding: 0, minWidth: 0 }}>
          {ATM_TABS.map((t) => (
            <button
              key={t.id}
              className={`pt-subtab amber ${tab === t.id ? "is-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        {showInlineActions && (
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button
              onClick={() => setRefreshConfirmOpen(true)}
              disabled={refreshing || saving}
              style={atmGhostBtn}
              title="Pull latest data from location summary"
            >
              {refreshing ? "Refreshing…" : "↻ Refresh"}
            </button>
            <button
              onClick={saveWeek}
              disabled={saving || refreshing}
              style={atmPrimaryBtn}
              title="Save all ATM rows"
            >
              {saving ? "Saving…" : "💾 Save"}
            </button>
          </div>
        )}
      </div>

      {feedback && (
        <div
          className={feedback.kind === "ok" ? "pt-card" : "pt-error"}
          style={{
            padding: "8px 14px",
            marginBottom: 10,
            color: feedback.kind === "ok" ? "var(--pt-green)" : undefined,
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {feedback.text}
        </div>
      )}

      {error && <div className="pt-error">{error}</div>}
      {loading && <Spinner label="Loading ATM…" />}

      {!loading && tab === "summary" && (
        <>
          <div className="pt-hero amber">
            <div className="lb">Total ATM This Week</div>
            <div className="big">{money0(sTot)}</div>
            <div className="stats">
              <div className="stat"><div><div className="k">ATM Cash</div><div className="v">{money0(sc)}</div></div></div>
              <div className="sep" />
              <div className="stat"><div><div className="k">GM Profit</div><div className="v">{money0(sg)}</div></div></div>
            </div>
          </div>
          <div className="pt-tbl-wrap">
            <div className="pt-tbl-scroll">
              <table className="pt-tbl">
                <thead><tr><th>ATM</th><th>ATM Cash</th><th>GM Profit</th><th>Total</th></tr></thead>
                <tbody>
                  {summary.map((r) => (
                    <tr key={r.name}>
                      <td>{r.name}</td>
                      <td>{money0(r.cash)}</td>
                      <td style={{ color: (Number(r.gm) || 0) > 0 ? "var(--pt-green)" : "var(--pt-muted-2)" }}>{money0(r.gm)}</td>
                      <td style={{ fontWeight: 800 }}>{money0((Number(r.cash) || 0) + (Number(r.gm) || 0))}</td>
                    </tr>
                  ))}
                  <tr className="total amber">
                    <td>TOTAL</td><td>{money0(sc)}</td><td>{money0(sg)}</td><td>{money0(sTot)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && tab === "lw" && (
        <>
          <div className="pt-hero blue">
            <div className="lb">ATM Last Week Total</div>
            <div className="big">{money0(lTot)}</div>
            <div className="stats">
              <div className="stat"><div><div className="k">ATM</div><div className="v">{money0(la)}</div></div></div>
              <div className="sep" />
              <div className="stat"><div><div className="k">Mid Week ADV</div><div className="v">{money0(lm)}</div></div></div>
            </div>
          </div>
          <div className="pt-tbl-wrap">
            <div className="pt-tbl-scroll">
              <table className="pt-tbl">
                <thead>
                  <tr>
                    <th>Manager</th>
                    <th>ATM Last Wk</th>
                    <th>Mid Wk ADV</th>
                    <th>Total</th>
                    {canEdit && <th style={{ textAlign: "right", width: 90 }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {lastWeekRows.map((r) => {
                    const mid = Number(r.midWeekAdv) || 0;
                    const tot = (Number(r.atmLastWeek) || 0) + mid;
                    return (
                      <tr key={r.manager}>
                        <td>{r.manager}</td>
                        <td>{money0(r.atmLastWeek)}</td>
                        <td style={{ color: mid > 0 ? "var(--pt-orange)" : "var(--pt-muted-2)" }}>
                          {canEdit && r.isEditing ? (
                            <NumInput
                              value={r.midWeekAdv}
                              onChange={(v) => updateLastWeekMidAdv(r.manager, v)}
                            />
                          ) : (
                            money0(mid)
                          )}
                        </td>
                        <td style={{ fontWeight: 800 }}>{money0(tot)}</td>
                        {canEdit && (
                          <td style={{ textAlign: "right" }}>
                            {r.isEditing ? (
                              <RowActionButtons
                                onCancel={() => cancelLastWeekEdit(r.manager)}
                                onConfirm={() => confirmLastWeekEdit(r.manager)}
                              />
                            ) : (
                              <IconRowBtn title="Edit" onClick={() => toggleLastWeekEdit(r.manager)}>✎</IconRowBtn>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  <tr className="total">
                    <td>TOTAL</td><td>{money0(la)}</td><td>{money0(lm)}</td><td>{money0(lTot)}</td>
                    {canEdit && <td />}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && tab === "nw" && (
        <>
          <div className="pt-hero green">
            <div className="lb">ATM Next Week Total</div>
            <div className="big">{money0(nTot)}</div>
            <div className="stats">
              <div className="stat"><div><div className="k">ATM Cash</div><div className="v">{money0(nc)}</div></div></div>
              <div className="sep" />
              <div className="stat"><div><div className="k">ATM ADV</div><div className="v">{money0(na)}</div></div></div>
              <div className="sep" />
              <div className="stat"><div><div className="k">Didn't Use</div><div className="v">{money0(nd)}</div></div></div>
            </div>
          </div>
          <div className="pt-tbl-wrap">
            <div className="pt-tbl-scroll">
              <table className="pt-tbl">
                <thead>
                  <tr>
                    <th>Manager</th>
                    <th>ATM Cash</th>
                    <th>ATM ADV</th>
                    <th>Didn't Use</th>
                    <th>Total</th>
                    {canEdit && <th style={{ textAlign: "right", width: 110 }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {autoRows.map((r) => (
                    <NextWeekRow
                      key={r.id}
                      row={r}
                      isManual={false}
                      canEdit={canEdit}
                      onUpdate={updateAutoField}
                      onToggleEdit={() => toggleAutoEdit(r.id)}
                      onCancel={() => cancelAutoEdit(r.id)}
                      onConfirm={() => confirmAutoEdit(r.id)}
                      onToggleLock={() => toggleAutoLock(r.id)}
                    />
                  ))}
                  {manualRows.map((r) => (
                    <NextWeekRow
                      key={r.id}
                      row={r}
                      isManual
                      canEdit={canEdit}
                      onUpdate={updateManualField}
                      onToggleEdit={() => toggleManualEdit(r.id)}
                      onCancel={() => cancelManualEdit(r.id)}
                      onConfirm={() => confirmManualEdit(r.id)}
                      onRemove={() => removeManualRow(r.id)}
                    />
                  ))}
                  <tr className="total">
                    <td>Total</td>
                    <td>{money0(nc)}</td>
                    <td>{money0(na)}</td>
                    <td>{money0(nd)}</td>
                    <td>{money0(nTot)}</td>
                    {canEdit && <td />}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {canEdit && (
            <button
              onClick={addManualRow}
              style={{
                marginTop: 10,
                background: "var(--pt-card)",
                border: "1.5px dashed var(--pt-teal)",
                color: "var(--pt-teal)",
                padding: "10px 14px",
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 13,
                fontFamily: "inherit",
                cursor: "pointer",
                width: "100%",
              }}
            >
              + Add manual row
            </button>
          )}
        </>
      )}

      {!loading && tab === "du" && (
        <>
          <div className="pt-hero amber">
            <div className="lb">Total Didn't Use</div>
            <div className="big">{money0(dt)}</div>
            <div className="stats">
              <div className="stat"><div><div className="k">Last Week</div><div className="v">{money0(lTot)}</div></div></div>
              <div className="sep" />
              <div className="stat"><div><div className="k">Total Use</div><div className="v">{money0(lTot - dt)}</div></div></div>
            </div>
          </div>

          <div className="pt-card" style={{ padding: 0, overflow: "hidden" }}>
            {duRows.length === 0 ? (
              <div style={{ padding: 16, color: "var(--pt-muted-2)", fontSize: 13, textAlign: "center" }}>
                No unused ATM allocations this week.
              </div>
            ) : (
              duRows.map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--pt-border)" }}>
                  <span style={{ color: "var(--pt-text-2)", fontSize: 13, fontWeight: 600 }}>{r.name}</span>
                  <span style={{ color: "var(--pt-amber-txt)", fontSize: 13, fontWeight: 700 }}>{money0(r.amt)}</span>
                </div>
              ))
            )}
          </div>

          {/* Summary footer — mirrors ATMReceipt's Total Didn't Use / Last Week / Total Use rows */}
          <div className="pt-card" style={{ padding: 0, overflow: "hidden", marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderBottom: "1px solid var(--pt-border)", background: "#fff7ed" }}>
              <span style={{ color: "var(--pt-text)", fontSize: 13, fontWeight: 700 }}>Total Didn't Use</span>
              <span style={{ color: "var(--pt-amber-txt)", fontSize: 16, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{money0(dt)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderBottom: "1px solid var(--pt-border)" }}>
              <span style={{ color: "var(--pt-text-2)", fontSize: 13, fontWeight: 600 }}>Last Week</span>
              <span style={{ color: "var(--pt-text)", fontSize: 15, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{money0(lTot)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: "#ecfdf5" }}>
              <span style={{ color: "var(--pt-text)", fontSize: 13, fontWeight: 700 }}>Total Use</span>
              <span style={{ color: "var(--pt-green)", fontSize: 16, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{money0(lTot - dt)}</span>
            </div>
          </div>
        </>
      )}

      {!hideInlineActions && (
        <ConfirmDialog
          open={refreshConfirmOpen}
          title="Refresh ATM Records"
          message="This will re-pull every row from the Manager Summary. Any in-progress edits on auto rows will be overwritten; manual rows and mid-week advances are preserved."
          confirmLabel={refreshing ? "Refreshing…" : "Refresh"}
          cancelLabel="Cancel"
          busy={refreshing}
          onClose={() => setRefreshConfirmOpen(false)}
          onConfirm={() => {
            setRefreshConfirmOpen(false);
            refresh();
          }}
        />
      )}
    </>
  );
}

// ─── ATM row helpers (shared by Last Week + Next Week edit UI) ───────────

const atmPrimaryBtn = {
  background: "var(--pt-teal)",
  border: "none",
  color: "#fff",
  padding: "8px 14px",
  borderRadius: 10,
  fontWeight: 600,
  fontSize: 12,
  fontFamily: "inherit",
  cursor: "pointer",
  whiteSpace: "nowrap",
  boxShadow: "var(--pt-shadow-sm)",
};

const atmGhostBtn = {
  background: "var(--pt-card)",
  border: "1px solid var(--pt-border)",
  color: "var(--pt-text-2)",
  padding: "8px 12px",
  borderRadius: 10,
  fontWeight: 600,
  fontSize: 12,
  fontFamily: "inherit",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const numInputStyle = {
  width: 90,
  boxSizing: "border-box",
  padding: "6px 8px",
  border: "1px solid var(--pt-teal)",
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  color: "var(--pt-text)",
  background: "#fff",
  outline: "none",
  textAlign: "right",
  fontFamily: "inherit",
  fontVariantNumeric: "tabular-nums",
};

function NumInput({ value, onChange }) {
  const display = value === 0 || value == null ? "" : value;
  return (
    <input
      type="number"
      value={display}
      placeholder="0"
      onChange={(e) => onChange(e.target.value)}
      style={numInputStyle}
    />
  );
}

function IconRowBtn({ title, onClick, danger, children }) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      style={{
        background: danger ? "#fef2f2" : "var(--pt-card)",
        border: `1px solid ${danger ? "#fecaca" : "var(--pt-border)"}`,
        color: danger ? "var(--pt-red)" : "var(--pt-text-2)",
        width: 30, height: 30, borderRadius: 8,
        cursor: "pointer",
        fontSize: 14, fontWeight: 700,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        marginLeft: 4,
      }}
    >
      {children}
    </button>
  );
}

function RowActionButtons({ onCancel, onConfirm }) {
  return (
    <div style={{ display: "inline-flex", justifyContent: "flex-end" }}>
      <IconRowBtn title="Cancel" onClick={onCancel} danger>✕</IconRowBtn>
      <IconRowBtn title="Confirm" onClick={onConfirm}>✓</IconRowBtn>
    </div>
  );
}

function NextWeekRow({ row, isManual, canEdit, onUpdate, onToggleEdit, onCancel, onConfirm, onRemove, onToggleLock }) {
  const cash = Number(row.eric) || 0;
  const adv = Number(row.profit) || 0;
  const didnt = Number(row.atmDidntUse) || 0;
  const total = cash + adv + didnt;

  if (canEdit && row.isEditing) {
    return (
      <tr>
        <td>
          <input
            type="text"
            value={row.manager || ""}
            onChange={(e) => onUpdate(row.id, "manager", e.target.value)}
            placeholder="Manager"
            style={{ ...numInputStyle, width: 120, textAlign: "left" }}
          />
        </td>
        <td><NumInput value={row.eric} onChange={(v) => onUpdate(row.id, "eric", v)} /></td>
        <td><NumInput value={row.profit} onChange={(v) => onUpdate(row.id, "profit", v)} /></td>
        <td><NumInput value={row.atmDidntUse} onChange={(v) => onUpdate(row.id, "atmDidntUse", v)} /></td>
        <td style={{ fontWeight: 800 }}>{money0(total)}</td>
        <td style={{ textAlign: "right" }}>
          <RowActionButtons onCancel={onCancel} onConfirm={onConfirm} />
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        {row.manager || <span style={{ color: "var(--pt-muted-2)", fontStyle: "italic" }}>(unnamed)</span>}
        {isManual && (
          <span style={{
            background: "#fff7ed", color: "var(--pt-amber-txt)",
            fontSize: 9, fontWeight: 700, padding: "2px 6px",
            borderRadius: 4, marginLeft: 8, textTransform: "uppercase", letterSpacing: 0.5,
          }}>
            Manual
          </span>
        )}
      </td>
      <td>{money0(cash)}</td>
      <td style={{ color: adv > 0 ? "var(--pt-orange)" : "var(--pt-muted-2)" }}>{money0(adv)}</td>
      <td style={{ color: didnt > 0 ? "var(--pt-amber-txt)" : "var(--pt-muted-2)" }}>{money0(didnt)}</td>
      <td style={{ fontWeight: 800 }}>{money0(total)}</td>
      {canEdit && (
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          <IconRowBtn title="Edit" onClick={onToggleEdit}>✎</IconRowBtn>
          {!isManual && onToggleLock && (
            <button
              onClick={onToggleLock}
              title={row.isLocked ? "LOCKED — click to unlock and allow refresh" : "UNLOCKED — click to lock and prevent refresh"}
              aria-label={row.isLocked ? "Locked" : "Unlocked"}
              style={{
                background: row.isLocked ? "#d32f2f" : "#e8f5e9",
                border: `1px solid ${row.isLocked ? "#b71c1c" : "#66bb6a"}`,
                color: row.isLocked ? "#fff" : "#2e7d32",
                height: 30,
                padding: "0 8px",
                borderRadius: 8,
                cursor: "pointer",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: 0.5,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                marginLeft: 4,
              }}
            >
              <span style={{ fontSize: 13 }}>{row.isLocked ? "🔒" : "🔓"}</span>
              {row.isLocked ? "LOCKED" : "UNLOCKED"}
            </button>
          )}
          {isManual && <IconRowBtn title="Remove" onClick={onRemove} danger>🗑</IconRowBtn>}
        </td>
      )}
    </tr>
  );
}

export function ATMDetail({ nav, ctx, api, locations, isAdmin }) {
  const { year, month, week } = ctx;
  const locationNames = useMemo(() => (locations || []).map((l) => l.name), [locations]);
  const data = useATMData(ctx, api, locationNames);
  return (
    <>
      <PTToolbar
        title={`ATM Records — ${week.id} · ${week.label}, ${year}`}
        onBack={() => nav("weeks", { year, month, mode: "atm" })}
      />
      <div className="pt-content">
        <ATMContent data={data} isAdmin={isAdmin} />
      </div>
    </>
  );
}
